// اللَّهُ لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ ۚ لَا تَأْخُذُهُ سِنَةٌ وَلَا نَوْمٌ ۚ
// لَهُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ ۗ مَنْ ذَا الَّذِي يَشْفَعُ عِنْدَهُ إِلَّا بِإِذْنِهِ ۚ
// يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَيْءٍ مِنْ عِلْمِهِ إِلَّا بِمَا شَاءَ ۚ
// وَسِعَ كُرْسِيُّهُ السَّمَاوَاتِ وَالْأَرْضَ ۖ وَلَا يَئُودُهُ حِفْظُهُمَا ۚ وَهُوَ الْعَلِيُّ الْعَظِيمُ
// (Bakara 255)

// Lookup / MultiLookup widget'ları hedef entity'den kayıt aramak için
// bu resolver üzerinden gider. UI package kendi implementasyonunu register eder.
// Bu sayede renderer package API client'a bağımlı kalmaz.

export interface LookupOption {
  readonly id: string;
  readonly label: string;
  /** Hiyerarşik katalogda düğümün derinliği (0=kök) — dropdown girintisi için. */
  readonly depth?: number;
  /** Hiyerarşik katalogda üst zincir etiketleri (kök→…→üst, seçili hariç) — yol gösterimi için. */
  readonly path?: readonly string[];
  /** Hedef katalogda "color" alanı varsa (LeadRating/OpportunityType/Stage) — renk noktası için hex. */
  readonly color?: string;
  /** Hedef katalogda "isDefault" alanı true ise — yeni kayıtta otomatik ön-dolum için. */
  readonly isDefault?: boolean;
  /** Hedef kayıtta "currency" alanı varsa (örn. AccountContract) — field.syncCurrencyToField ile kardeş alana aktarılır. */
  readonly currency?: string;
  /** Hedef katalogda "isPrimary" alanı true ise (örn. birincil adres) — otomatik ön-dolum için. */
  readonly isPrimary?: boolean;
  /**
   * Hedef kaydın ham alanları — field.inheritFrom ile AYNI ADLI alanı belgeye taşımak için
   * (Cari.paymentTermDays → Fatura.paymentTermDays). Her yeni miras alanı için bu arayüze
   * yeni bir özellik eklemek gerekmesin diye tek torba.
   */
  readonly extra?: Readonly<Record<string, unknown>>;
}

/** Sonucu daraltan kardeş-alan filtresi — field: HEDEF entity'deki kolon adı, value: filtre değeri. */
export interface LookupFilter {
  readonly field: string;
  readonly value: string | undefined;
}

export type LookupResolver = (
  target: string,
  query: string,
  signal?: AbortSignal,
  filter?: LookupFilter,
) => Promise<readonly LookupOption[]>;

export type LookupByIdResolver = (
  target: string,
  id: string,
) => Promise<LookupOption | null>;

/**
 * Bir hedefin BİRDEN ÇOK kimliğini TEK istekte çözer. Liste ekranında her satır için ayrı
 * istek atılıyordu (25 satır = 13 ayrı çağrı); etiketler gecikince kullanıcı bir an ham GUID
 * görüyordu. Toplu çözümde etiketler tek turda gelir.
 */
export type LookupByIdsResolver = (
  target: string,
  ids: readonly string[],
) => Promise<readonly LookupOption[]>;

let _resolver: LookupResolver | null = null;
let _byIdResolver: LookupByIdResolver | null = null;
let _byIdsResolver: LookupByIdsResolver | null = null;

export function registerLookupResolver(resolver: LookupResolver): void {
  _resolver = resolver;
}

export function registerLookupByIdResolver(resolver: LookupByIdResolver): void {
  _byIdResolver = resolver;
}

export function registerLookupByIdsResolver(resolver: LookupByIdsResolver): void {
  _byIdsResolver = resolver;
}

// Kullanici/sirket/sube gibi PLATFORM kayitlari metadata varligi degil: acilir listede
// secilebilirler ama tam izgara ("Sec (F4)"), hizli olusturma ve kart acma metadata ister,
// sunucu 404 doner ve dugme sessizce hicbir sey yapmazdi. Hangi hedeflerin platform oldugunu
// UI paketi bildirir; renderer yalniz sorar.
let _platformTargetCheck: ((target: string) => boolean) | null = null;

export function registerPlatformLookupTargetCheck(check: (target: string) => boolean): void {
  _platformTargetCheck = check;
}

export function isPlatformLookupTarget(target: string | undefined): boolean {
  return !!target && !!_platformTargetCheck && _platformTargetCheck(target);
}

export async function resolveLookup(
  target: string,
  query: string,
  signal?: AbortSignal,
  filter?: LookupFilter,
): Promise<readonly LookupOption[]> {
  if (!_resolver) return [];

  // BOŞ sorgu = "listeyi aç" durumu; aynı liste aynı formda defalarca istenir (her açılışta,
  // her alanda). Sunucu 20-50 ms'de dönse bile her açılışta ağ turu beklemek listenin boş
  // görünmesine yol açıyor — kullanıcı bunu "geç açılıyor" diye okuyor. Yazılan sorgular
  // önbelleğe ALINMAZ: onlar zaten değişken ve tazelik ister.
  if (query) return _resolver(target, query, signal, filter);

  const anahtar = onbellekAnahtari(target, filter);
  const kayit = _onbellek.get(anahtar);
  if (kayit && Date.now() - kayit.zaman < ONBELLEK_SURESI_MS) return kayit.sonuc;

  const ucustaki = _ucustakiler.get(anahtar);
  if (ucustaki) return ucustaki;

  const istek = _resolver(target, query, signal, filter)
    .then((sonuc) => {
      _onbellek.set(anahtar, { zaman: Date.now(), sonuc });
      return sonuc;
    })
    .finally(() => { _ucustakiler.delete(anahtar); });

  _ucustakiler.set(anahtar, istek);
  return istek;
}

const ONBELLEK_SURESI_MS = 30_000;
const _onbellek = new Map<string, { zaman: number; sonuc: readonly LookupOption[] }>();
const _ucustakiler = new Map<string, Promise<readonly LookupOption[]>>();

function onbellekAnahtari(target: string, filter?: LookupFilter): string {
  return filter ? `${target}|${filter.field}=${String(filter.value ?? '')}` : target;
}

/**
 * Liste açılmadan ÖNCE veriyi hazırlar (alanın üzerine gelindiğinde/odaklanıldığında çağrılır).
 * Kullanıcı tıkladığında liste önbellekten anında gelir; ağ turu beklenmez.
 */
export function preloadLookup(target: string, filter?: LookupFilter): void {
  if (!_resolver || !target) return;
  const anahtar = onbellekAnahtari(target, filter);
  if (_onbellek.has(anahtar) || _ucustakiler.has(anahtar)) return;
  void resolveLookup(target, '', undefined, filter).catch(() => {});
}

// Kimlik -> görünen ad. Liste ekranı ayrıldığında/yeniden çizildiğinde aynı cari adları
// tekrar sorulmasın; liste cevabıyla gelen adlar da buraya yazılır (rememberLookupLabels).
const _etiketler = new Map<string, string>();
const _ucustakiKimlikler = new Map<string, Promise<readonly LookupOption[]>>();

export function cachedLookupLabel(target: string, id: string): string | undefined {
  return _etiketler.get(`${target}::${id}`);
}

export function rememberLookupLabels(target: string, opts: readonly { id: string; label: string }[]): void {
  for (const o of opts) _etiketler.set(`${target}::${o.id}`, o.label);
}

/** Yazma sonrası çağrılır: yeni/değişen kayıt listede hemen görünsün. */
export function clearLookupCache(): void {
  _onbellek.clear();
  _ucustakiler.clear();
  _etiketler.clear();
  _ucustakiKimlikler.clear();
}

export async function resolveLookupById(
  target: string,
  id: string,
): Promise<LookupOption | null> {
  if (!_byIdResolver) return null;
  return _byIdResolver(target, id);
}

/** Aynı hedefin birden çok kimliğini tek turda çözer; kayıtlı toplu çözücü yoksa boş döner. */
export async function resolveLookupByIds(
  target: string,
  ids: readonly string[],
): Promise<readonly LookupOption[]> {
  if (!_byIdsResolver || ids.length === 0) return [];
  // Aynı kimlikler zaten soruluyorsa o cevap beklenir; liste art arda iki kez çizilince
  // cari adları iki kez isteniyordu (05.10.2026 ölçüldü).
  const anahtar = `${target}|${[...ids].sort().join(',')}`;
  const ucustaki = _ucustakiKimlikler.get(anahtar);
  if (ucustaki) return ucustaki;
  const istek = _byIdsResolver(target, ids)
    .then((sonuc) => { rememberLookupLabels(target, sonuc); return sonuc; })
    .finally(() => { _ucustakiKimlikler.delete(anahtar); });
  _ucustakiKimlikler.set(anahtar, istek);
  return istek;
}
